import Enum from '/@/modules/core/app/helpers/Enum';

export class PredefinedEstateRole extends Enum {
  id!: number;
  key!: string;
  value!: string;

  static EXECUTOR: PredefinedEstateRole = {
    id: 1,
    key: 'executor',
    value: 'Executor'
  };

  static EXECUTOR_AND_BENEFICIARY: PredefinedEstateRole = {
    id: 2,
    key: 'executor_and_beneficiary',
    value: 'Executor & Beneficiary'
  };

  static BENEFICIARY: PredefinedEstateRole = {
    id: 3,
    key: 'beneficiary',
    value: 'Beneficiary'
  };

  static ESTATE_PROFESSIONAL: PredefinedEstateRole = {
    id: 4,
    key: 'estate_professional',
    value: 'Estate Professional'
  };

  static PROFESSIONAL: PredefinedEstateRole = {
    id: 5,
    key: 'professional',
    value: 'Professional'
  };

  static PLANNER: PredefinedEstateRole = {
    id: 6,
    key: 'planner',
    value: 'Planner'
  };

  static DEPENDANT: PredefinedEstateRole = {
    id: 7,
    key: 'dependant',
    value: 'Dependant'
  };

  static EXECUTOR_AND_DEPENDANT: PredefinedEstateRole = {
    id: 8,
    key: 'executor_and_dependant',
    value: 'Executor'
  };

  static DEFAULT: PredefinedEstateRole = {
    id: 9,
    key: 'default',
    value: 'Default'
  };

  static EXECUTOR_PROFESSIONAL: PredefinedEstateRole = {
    id: 10,
    key: 'executor_professional',
    value: 'Professional Executor'
  };

  static isExecutor(roleId: number): boolean {
    return [
      this.EXECUTOR.id,
      this.EXECUTOR_AND_BENEFICIARY.id,
      this.EXECUTOR_AND_DEPENDANT.id,
      this.EXECUTOR_PROFESSIONAL.id
    ].includes(roleId);
  }

  static isOnlyExecutor(roleId: number): boolean {
    return roleId === this.EXECUTOR.id;
  }

  static isBeneficiary(roleId: number): boolean {
    return roleId === this.BENEFICIARY.id || roleId === this.EXECUTOR_AND_BENEFICIARY.id;
  }

  static isOnlyBeneficiary(roleId: number): boolean {
    return roleId === this.BENEFICIARY.id;
  }

  static isExecutorAndBeneficiary(roleId: number): boolean {
    return roleId === this.EXECUTOR_AND_BENEFICIARY.id;
  }

  static hasProfessionalRole(roleId: number): boolean {
    return [
      this.PROFESSIONAL.id,
      this.ESTATE_PROFESSIONAL.id,
      this.EXECUTOR_PROFESSIONAL.id
    ].includes(roleId);
  }

  static isProfessional(roleId: number): boolean {
    return roleId === this.PROFESSIONAL.id;
  }

  static isEstateProfessional(roleId: number): boolean {
    return roleId === this.ESTATE_PROFESSIONAL.id;
  }

  static isPlanner(roleId: number): boolean {
    return roleId === this.PLANNER.id;
  }

  static isDependant(roleId: number): boolean {
    return roleId === this.DEPENDANT.id;
  }

  static isExecutorAndDependant(roleId: number): boolean {
    return roleId === this.EXECUTOR_AND_DEPENDANT.id;
  }

  static isExecutorProfessional(roleId: number): boolean {
    return roleId === this.EXECUTOR_PROFESSIONAL.id;
  }

  static isDefault(roleId: number): boolean {
    return roleId === this.DEFAULT.id;
  }

  static isEstateMember(roleId: number): boolean {
    return this.getEstateMemberRoleIds().includes(roleId);
  }

  static getEstateRoleIds(): number[] {
    return [...this.getEstateMemberRoleIds(), this.PROFESSIONAL.id, this.DEFAULT.id];
  }

  static getExecutorRoleIds(): number[] {
    return [
      this.EXECUTOR.id,
      this.EXECUTOR_AND_BENEFICIARY.id,
      this.EXECUTOR_AND_DEPENDANT.id,
      this.EXECUTOR_PROFESSIONAL.id
    ];
  }

  static getBeneficiaryRoleIds(): number[] {
    return [this.BENEFICIARY.id, this.EXECUTOR_AND_BENEFICIARY.id];
  }

  static getDependantRoleIds(): number[] {
    return [this.DEPENDANT.id, this.EXECUTOR_AND_DEPENDANT.id];
  }

  static getEstateMemberRoleIds(): number[] {
    return [
      this.EXECUTOR.id,
      this.EXECUTOR_AND_BENEFICIARY.id,
      this.BENEFICIARY.id,
      this.DEPENDANT.id,
      this.EXECUTOR_AND_DEPENDANT.id,
      this.PLANNER.id
    ];
  }

  static getNonPlannerEstateMemberRoleIds(): number[] {
    return [
      this.EXECUTOR.id,
      this.EXECUTOR_AND_BENEFICIARY.id,
      this.BENEFICIARY.id,
      this.DEPENDANT.id,
      this.EXECUTOR_AND_DEPENDANT.id,
      this.DEFAULT.id
    ];
  }

  static getAnnouncementEstateRoles(): PredefinedEstateRole[] {
    return [this.EXECUTOR, this.PLANNER, this.BENEFICIARY, this.ESTATE_PROFESSIONAL];
  }

  static getAnnouncementEstateRoleIds(): number[] {
    return this.getAnnouncementEstateRoles().map(
      (estateRole: PredefinedEstateRole) => estateRole.id
    );
  }
}
